import React from "react";
import styled, { css } from "styled-components";

import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils";

import SelectedUserGroup from "./SelectedUserGroup";
import SelectedUser from "./SelectedUser";
import {
  IUser,
  IUserGroup
} from "../api/users.api";

interface AssignUserAndGroupSelectProps {
  /** provides user group data to display when a group is selected */
  selectedUserGroup: IUserGroup | null;
  /** provides user data to display when a user is selected */
  assignedUser: IUser | null;
  /** provides terminology localisation */
  terminology: ITerminology;
  /** callback to handle update of selected user or group */
  handleUpdate: (updateType: "userGroupId" | "assignedUserId", updateValue: number | null) => void;
}

/**
 * @description Shows two select fields to pick an assigned user or user group and fires the handleUpdate callback upon selection
 */
const AssignUserAndGroupSelect: React.FC<AssignUserAndGroupSelectProps> = ({
  selectedUserGroup, assignedUser, terminology, handleUpdate
}) => {
  return (
    <AssignUserAndGroupSelectWrapper>
      <div className="assigned-to-container">
        <div className="sub-header">
          <Text weight="bold">
            Assigned To
          </Text>
        </div>

        <div className="user-group-container">
          <Text>
            User Group
          </Text>

          <SelectedUserGroup
            userGroup={selectedUserGroup}
            handleUpdate={userGroup => {
              if (selectedUserGroup?.id !== userGroup?.id) {
                handleUpdate("userGroupId", userGroup?.id || null);
              }
            }}
          />
        </div>

        <div className="assigned-user">
          <Text>
            Assigned User
          </Text>

          <SelectedUser
            user={assignedUser}
            handleUpdate={user => {
              if (assignedUser?.id !== user?.id) {
                handleUpdate("assignedUserId", user?.id || null);
              }
            }}
            terminology={terminology}
            userGroupId={selectedUserGroup?.id}
          />
        </div>
      </div>
    </AssignUserAndGroupSelectWrapper>
  );
};

const AssignUserAndGroupSelectWrapper = styled.div`
  ${({ theme }) => css`
    .assigned-to-container {
      margin: ${theme.spacing.margins.sm};
      padding: ${theme.spacing.margins.sm};

      .sub-header {
        margin-bottom: ${theme.spacing.margins.md};
      }

      .user-group-container,
      .assigned-user {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: ${theme.spacing.margins.md};
      }

      .assigned-user {
        span {
          margin-right: ${theme.spacing.margins.sm};
        }

        .selected-user {
          min-width: 160px;
          border: solid 1px ${theme.colors.lightGrey};
          padding: 0 ${theme.spacing.padding.md};
          height: 36px;

          &:hover,
          &:focus {
            border-color: ${theme.colors.primaryDark};
            background-color: ${theme.colors.white};
          }
        }
      }
    }
  `}
`;

export default AssignUserAndGroupSelect;